/**
 * Buttons (docs/04 §3): primary / secondary / gold-outline / ghost /
 * danger-ghost. One primary per view (enforced by review, linted visually via
 * the /design baseline). Disabled-with-reason is the accessible pattern —
 * docs/04 §7 bans disabled buttons without reasons.
 */

import { clsx } from 'clsx';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'gold-outline' | 'ghost' | 'danger-ghost';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'> {
  readonly variant?: ButtonVariant | undefined;
  /** Shows the in-button spinner and blocks re-submit (docs/03 §2). */
  readonly busy?: boolean | undefined;
  /** Disabling requires a human-readable reason (docs/04 §7). */
  readonly disabledReason?: string | undefined;
  readonly children: ReactNode;
}

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-brand-700 text-on-brand hover:bg-brand-600 border border-transparent',
  secondary: 'bg-card text-ink border border-control-line hover:border-ink2',
  'gold-outline': 'bg-card text-gold-text border border-gold-deep hover:bg-gold-tint',
  ghost: 'bg-transparent text-ink2 border border-transparent hover:bg-surface-hover',
  'danger-ghost': 'bg-transparent text-red-fg border border-transparent hover:bg-red-bg',
};

/**
 * A disabled button must LOOK inert, not merely be inert.
 *
 * `opacity-60` over the variant was the old treatment, and it failed twice: a
 * 60% brand-green button with white text still reads as the call to action —
 * Matt clicked one in a dialog and reported the feature broken — and fading a
 * token PAIR is exactly what docs/04 §1 forbids, because it drops a
 * deliberately AA-verified combination to an unverified one. So the disabled
 * state swaps to the neutral pair (already contrast-checked, the same one the
 * neutral chip uses) and drops every hover, which says "inert" at a glance
 * without inventing a colour.
 *
 * BUSY is not this: a saving button keeps its variant, because it is mid-action
 * rather than unavailable, and a spinner on a greyed-out button reads as broken.
 */
const DISABLED: Record<ButtonVariant, string> = {
  primary: 'bg-neut-bg text-neut-fg border border-control-line',
  secondary: 'bg-neut-bg text-neut-fg border border-control-line',
  'gold-outline': 'bg-neut-bg text-neut-fg border border-control-line',
  // The ghosts have no fill to grey; muting the text is the whole signal.
  ghost: 'bg-transparent text-neut-fg border border-transparent',
  'danger-ghost': 'bg-transparent text-neut-fg border border-transparent',
};

/**
 * The classes that make something LOOK like a button, shared with anything that
 * has to look like one without being one.
 *
 * A "New vendor" control that NAVIGATES should be an `<a>`: middle-click, open
 * in a new tab, copy link address, and the status bar showing where it goes are
 * all things a `<button onClick={navigate}>` silently takes away. So four
 * screens rendered a react-router `<Link>` with the primary variant's classes
 * copied out by hand — the right element, with a copy of the styling.
 *
 * And the copy had already drifted from the original in four ways, every one of
 * them visible:
 *
 *   * no `focus-visible:outline-*`, so four primary calls-to-action had a
 *     different focus indicator from every other button in the app;
 *   * no `border border-transparent`, so they stood 2px shorter than a real
 *     primary button sitting next to them;
 *   * no `justify-center gap-2`;
 *   * no `transition-colors`, so the hover snapped while everything else eased.
 *
 * Exported as classes rather than as a `LinkButton` component on purpose: the
 * design system must not import the router (the rule `RouterAnchor` exists for),
 * and a `LinkComponent` prop for four call sites is more machinery than the
 * problem needs. What drifted was the class list, so the class list is what is
 * shared.
 */
export function buttonClasses(variant: ButtonVariant = 'secondary'): string {
  return clsx(BUTTON_BASE, VARIANT[variant]);
}

/** `buttonClasses` for an anchor, which needs its underline removed. */
export function linkButtonClasses(variant: ButtonVariant = 'secondary'): string {
  return clsx(buttonClasses(variant), 'no-underline');
}

const BUTTON_BASE = clsx(
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-ctl px-4 py-2',
  'text-body font-medium transition-colors duration-(--dur-fast)',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700',
);

export function Button({
  variant = 'secondary',
  busy = false,
  disabledReason,
  className,
  children,
  type,
  ...rest
}: ButtonProps): ReactNode {
  const disabled = busy || disabledReason !== undefined;
  const unavailable = !busy && disabledReason !== undefined;
  return (
    <button
      type={type ?? 'button'}
      className={clsx(
        BUTTON_BASE,
        unavailable ? DISABLED[variant] : VARIANT[variant],
        disabled && 'cursor-not-allowed',
        className,
      )}
      disabled={disabled}
      aria-busy={busy || undefined}
      title={disabledReason}
      aria-disabled={disabled || undefined}
      {...rest}
    >
      {busy ? (
        <span
          aria-hidden
          className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      ) : null}
      {children}
      {disabledReason !== undefined ? <span className="sr-only"> — {disabledReason}</span> : null}
    </button>
  );
}
